// src/components/chat/AttachmentStrip.tsx
//
// Horizontal scrollable strip of attachment thumbnails, each showing
// its own upload progress, error state, or "ready" check. Sits above
// the composer like ChatGPT/Claude's drop strip.

import React from "react";
import {
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close,
  ErrorOutline,
  Refresh,
  CheckCircle,
  CheckCircleOutline,
} from "@mui/icons-material";
import { Attachment } from "../../hooks/useAttachments";
import { useT } from '../../i18n/useT';

interface AttachmentStripProps {
  attachments: Attachment[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  /** Optional "Post to feed" toggle. When provided, a small toggle
   *  row renders below the thumbnail strip — only when there's at
   *  least one completed image attachment. Toggling it doesn't post
   *  immediately; the parent reads the state at send time and writes
   *  a feed_item per completed image. Visibility defaults to the
   *  user's photo default (public unless customized). */
  postToFeed?: boolean;
  onTogglePostToFeed?: (next: boolean) => void;
}

const TILE_SIZE = 64;

const Tile: React.FC<{
  attachment: Attachment;
  onRemove: () => void;
  onRetry: () => void;
}> = ({ attachment, onRemove, onRetry }) => {
  const t = useT();
  const { status, progress, previewUrl, error } = attachment;

  const showSpinner =
    status === "preparing" ||
    status === "uploading" ||
    status === "retrying";

  return (
    <Box
      sx={{
        position: "relative",
        width: TILE_SIZE,
        height: TILE_SIZE,
        flexShrink: 0,
        borderRadius: 1.5,
        overflow: "hidden",
        border: 1,
        borderColor: status === "error" ? "error.main" : "divider",
        bgcolor: "action.hover",
      }}
    >
      {/* Thumbnail */}
      <Box
        component="img"
        src={previewUrl}
        alt=""
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          opacity: status === "complete" ? 1 : 0.7,
        }}
      />

      {/* In-flight overlay with spinner + progress text */}
      {showSpinner && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.35)",
            color: "common.white",
          }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              size={28}
              variant={progress > 0 ? "determinate" : "indeterminate"}
              value={progress}
              sx={{ color: "common.white" }}
            />
            {progress > 0 && (
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: "common.white",
                  }}
                >
                  {Math.round(progress)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Error overlay with retry button */}
      {status === "error" && (
        <Tooltip title={error || "Upload failed — tap to retry"}>
          <Box
            onClick={onRetry}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(211,47,47,0.55)",
              color: "common.white",
              cursor: "pointer",
              gap: 0.25,
            }}
          >
            <ErrorOutline sx={{ fontSize: 18 }} />
            <Refresh sx={{ fontSize: 14 }} />
          </Box>
        </Tooltip>
      )}

      {/* Completed checkmark */}
      {status === "complete" && (
        <Box
          sx={{
            position: "absolute",
            bottom: 2,
            left: 2,
            color: "success.main",
            bgcolor: "common.white",
            borderRadius: "50%",
            display: "flex",
          }}
        >
          <CheckCircle sx={{ fontSize: 16 }} />
        </Box>
      )}

      {/* Remove × button */}
      <IconButton
        onClick={onRemove}
        size="small"
        aria-label={t("Remove attachment")}
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          color: "common.white",
          width: 20,
          height: 20,
          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
        }}
      >
        <Close sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
};

export const AttachmentStrip: React.FC<AttachmentStripProps> = ({
  attachments,
  onRemove,
  onRetry,
  postToFeed = false,
  onTogglePostToFeed,
}) => {
  const t = useT();
  if (attachments.length === 0) return null;

  // Toggle is meaningful only when at least one attachment has
  // finished uploading. While everything is still in flight, the
  // user can't yet decide what to do with photos that don't exist
  // server-side.
  const hasCompletedImage = attachments.some((a) => a.status === 'complete');
  const showToggle = !!onTogglePostToFeed && hasCompletedImage;

  return (
    <Box sx={{ mx: 2, mb: 1, flexShrink: 0 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          py: 0.5,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {attachments.map((a) => (
          <Tile
            key={a.id}
            attachment={a}
            onRemove={() => onRemove(a.id)}
            onRetry={() => onRetry(a.id)}
          />
        ))}
      </Box>
      {showToggle && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 0.25,
            cursor: 'pointer',
            color: postToFeed ? 'primary.main' : 'text.secondary',
            userSelect: 'none',
          }}
          onClick={() => onTogglePostToFeed?.(!postToFeed)}
        >
          {postToFeed ? (
            <CheckCircle sx={{ fontSize: 16, mr: 0.5 }} />
          ) : (
            <CheckCircleOutline sx={{ fontSize: 16, mr: 0.5 }} />
          )}
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {postToFeed
              ? t('Will post to your feed')
              : t('Also post to your feed')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
